import React, { useState, useEffect, useRef, memo } from 'react';

const TTT_LINES = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
const checkWin = (b) => {
  for (let [a,i,j] of TTT_LINES) {
    if (b[a] && b[a]===b[i] && b[a]===b[j]) return {w:b[a],line:[a,i,j]};
  }
  return b.every(Boolean) ? {w:'Draw',line:[]} : null;
};

const API = '/api';

// Stable cell component - won't re-render unless cell value changes
const Cell = memo(({ value, isWin, onClick, size }) => (
  <div onClick={onClick} style={{
    width: size, height: size, borderRadius: '12px',
    border: `3px solid ${isWin ? '#34d399' : '#6200ea'}`,
    background: isWin ? '#34d399' : value==='X' ? '#f3e5f5' : value==='O' ? '#fff3e0' : '#fafafa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size*0.48, fontWeight: 'bold', cursor: !value ? 'pointer' : 'default',
    color: value==='X' ? '#6200ea' : '#0d9488',
  }}>{value}</div>
));

export default function OnlineGame({ user }) {
  const [screen, setScreen] = useState('lobby');
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [mySymbol, setMySymbol] = useState('X');
  const [board, setBoard] = useState(Array(9).fill(null));
  const [turn, setTurn] = useState('X');
  const [result, setResult] = useState(null);
  const [opponent, setOpponent] = useState('');
  const [status, setStatus] = useState('');

  const pollingRef = useRef(null);
  const boardRef = useRef(JSON.stringify(Array(9).fill(null)));
  const roomRef = useRef('');
  const symbolRef = useRef('X');
  const myName = user?.username || 'Guest';

  const cs = Math.floor((Math.min(typeof window!=='undefined'?window.innerWidth:380,380)-60)/3);

  const pushState = async (room, state) => {
    await fetch(`${API}/game/${room}/`, {
      method: 'POST',
      headers: {'Content-Type':'application/json', 'Authorization': user?.token ? `Token ${user.token}` : ''},
      body: JSON.stringify(state)
    });
  };

  const startPolling = (room, symbol) => {
    clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/game/${room}/`);
        if (!res.ok) return;
        const state = await res.json();
        if (!state?.board) return;

        const newBoardStr = JSON.stringify(state.board);
        
        // Update opponent name
        if (state.player1 && state.player2) {
          const opp = state.player1===myName ? state.player2 : state.player1;
          setOpponent(prev => prev===opp ? prev : opp);
          setScreen(prev => prev==='waiting' ? 'playing' : prev);
          setStatus('');
        }

        // Detect quit
        if (state.status === 'quit' && state.quitter !== myName) {
          clearInterval(pollingRef.current);
          setStatus(`@${state.quitter} left the game!`);
          setScreen('result');
          setResult({w: 'quit', line: []});
          return;
        }

        // Detect restart from opponent
        if (state.status === 'playing' && result) {
          boardRef.current = JSON.stringify(Array(9).fill(null));
          setBoard(Array(9).fill(null));
          setTurn('X');
          setResult(null);
          setScreen('playing');
          setStatus('');
          return;
        }

        // Only update board if changed
        if (newBoardStr === boardRef.current) return;
        boardRef.current = newBoardStr;
        setBoard([...state.board]);
        setTurn(state.turn || 'X');

        const r = checkWin(state.board);
        if (r) { setResult(r); setScreen('result'); clearInterval(pollingRef.current); }
      } catch(e) {}
    }, 1500);
  };

  const createRoom = async () => {
    const code = 'G' + Math.random().toString(36).substr(2,5).toUpperCase();
    const initState = { board: Array(9).fill(null), turn:'X', player1: myName, player2: null };
    await pushState(code, initState);
    setRoomCode(code);
    setMySymbol('X');
    symbolRef.current = 'X';
    roomRef.current = code;
    boardRef.current = JSON.stringify(Array(9).fill(null));
    startPolling(code, 'X');
    setScreen('waiting');
    setStatus('Waiting for opponent...');
  };

  const joinRoom = async () => {
    if (!joinCode.trim()) return;
    const code = joinCode.trim().toUpperCase();
    setStatus('Joining...');
    
    let state = null;
    for (let i=0; i<5; i++) {
      try {
        const res = await fetch(`${API}/game/${code}/`);
        if (res.ok) {
          const data = await res.json();
          if (data?.player1) { state = data; break; }
        }
      } catch(e) {}
      await new Promise(r => setTimeout(r, 800));
    }

    if (!state) { setStatus('Room not found!'); return; }

    const newState = {...state, player2: myName};
    await pushState(code, newState);
    setRoomCode(code);
    setMySymbol('O');
    symbolRef.current = 'O';
    roomRef.current = code;
    setOpponent(state.player1);
    boardRef.current = JSON.stringify(state.board || Array(9).fill(null));
    setBoard(state.board || Array(9).fill(null));
    setTurn(state.turn || 'X');
    startPolling(code, 'O');
    setScreen('playing');
    setStatus('');
  };

  const makeMove = async (i) => {
    if (board[i] || result || turn !== mySymbol) return;
    const nb = [...board];
    nb[i] = mySymbol;
    const next = mySymbol==='X' ? 'O' : 'X';
    boardRef.current = JSON.stringify(nb);
    setBoard(nb);
    setTurn(next);
    const newState = {
      board: nb, turn: next,
      player1: mySymbol==='X' ? myName : opponent,
      player2: mySymbol==='O' ? myName : opponent,
    };
    await pushState(roomCode, newState);
    const r = checkWin(nb);
    if (r) { setResult(r); setScreen('result'); clearInterval(pollingRef.current); }
  };

  const restart = async () => {
    const newState = {
      board: Array(9).fill(null), turn: 'X',
      player1: mySymbol==='X' ? myName : opponent,
      player2: mySymbol==='O' ? myName : opponent,
      status: 'playing',
      ts: Date.now(),
    };
    boardRef.current = JSON.stringify(Array(9).fill(null));
    await pushState(roomCode, newState);
    setBoard(Array(9).fill(null));
    setTurn('X');
    setResult(null);
    setScreen('playing');
    startPolling(roomCode, mySymbol);
  };

  const exit = async () => {
    if (roomCode) {
      const quitState = {
        board: board, turn: turn,
        player1: mySymbol==='X' ? myName : opponent,
        player2: mySymbol==='O' ? myName : opponent,
        status: 'quit',
        quitter: myName,
        ts: Date.now(),
      };
      await pushState(roomCode, quitState);
    }
    clearInterval(pollingRef.current);
    setScreen('lobby'); setBoard(Array(9).fill(null)); setTurn('X');
    setResult(null); setOpponent(''); setRoomCode(''); setJoinCode(''); setStatus('');
    boardRef.current = JSON.stringify(Array(9).fill(null));
  };

  useEffect(() => () => clearInterval(pollingRef.current), []);

  const G = { btn: {backgroundColor:'#6200ea',color:'white',border:'none',padding:'12px 20px',borderRadius:'20px',cursor:'pointer',fontWeight:'bold',fontSize:'14px'} };

  if (!user) return <div style={{textAlign:'center',padding:'30px'}}><h3 style={{color:'#6200ea'}}>🔑 Login Required</h3></div>;

  if (screen==='lobby') return (
    <div style={{textAlign:'center'}}>
      <div style={{fontSize:'48px',marginBottom:'8px'}}>❌⭕</div>
      <h3 style={{color:'#6200ea',marginBottom:'6px'}}>TicTacToe Online</h3>
      <p style={{color:'#999',fontSize:'13px',marginBottom:'20px'}}>@{myName}</p>
      <div style={{display:'flex',flexDirection:'column',gap:'12px',maxWidth:'280px',margin:'0 auto'}}>
        <button style={{...G.btn,backgroundColor:'#10b981'}} onClick={createRoom}>🏠 Create Room (X)</button>
        <div style={{backgroundColor:'#f3e5f5',padding:'14px',borderRadius:'12px'}}>
          <p style={{color:'#666',fontSize:'13px',margin:'0 0 8px'}}>Join as O:</p>
          <div style={{display:'flex',gap:'8px'}}>
            <input style={{flex:1,padding:'10px',borderRadius:'10px',border:'2px solid #ddd',fontSize:'14px'}}
              placeholder="Room code..." value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())}/>
            <button style={{...G.btn,padding:'10px 14px'}} onClick={joinRoom}>Join</button>
          </div>
        </div>
        {status && <p style={{color:'#ff4444',fontSize:'13px'}}>{status}</p>}
      </div>
    </div>
  );

  if (screen==='waiting') return (
    <div style={{textAlign:'center',padding:'20px'}}>
      <div style={{fontSize:'40px',marginBottom:'12px'}}>⏳</div>
      <h3 style={{color:'#6200ea'}}>Room Created!</h3>
      <div style={{backgroundColor:'#f3e5f5',padding:'16px',borderRadius:'12px',margin:'12px 0'}}>
        <div style={{fontSize:'12px',color:'#999',marginBottom:'4px'}}>Share this code:</div>
        <div style={{fontSize:'28px',fontWeight:'bold',color:'#6200ea',letterSpacing:'4px'}}>{roomCode}</div>
      </div>
      <p style={{color:'#10b981',fontSize:'13px'}}>{status}</p>
      <button style={{...G.btn,backgroundColor:'#999',marginTop:'10px'}} onClick={exit}>Cancel</button>
    </div>
  );

  if (screen==='result') return (
    <div style={{textAlign:'center'}}>
      <div style={{fontSize:'48px'}}>{result?.w==='quit'?'🚪':result?.w===mySymbol?'🎉':result?.w==='Draw'?'🤝':'😢'}</div>
      <h3 style={{color:'#6200ea',marginBottom:'6px'}}>
        {result?.w==='quit' ? status : result?.w==='Draw' ? "It's a Draw!" : result?.w===mySymbol ? `@${myName} Wins!` : `@${opponent} Wins!`}
      </h3>
      {result?.w !== 'quit' && (
        <div style={{display:'flex',gap:'10px',justifyContent:'center',marginTop:'12px'}}>
          <button style={G.btn} onClick={restart}>Play Again</button>
          <button style={{...G.btn,backgroundColor:'#999'}} onClick={exit}>Exit</button>
        </div>
      )}
      {result?.w === 'quit' && (
        <button style={{...G.btn,marginTop:'12px'}} onClick={exit}>Back to Lobby</button>
      )}
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'12px'}}>
      <div style={{display:'flex',gap:'16px',backgroundColor:'#f3e5f5',padding:'8px 16px',borderRadius:'20px',fontSize:'13px'}}>
        <span style={{color:'#6200ea',fontWeight:'bold'}}>@{myName} ({mySymbol}) You</span>
        <span style={{color:'#999'}}>vs</span>
        <span style={{color:'#0d9488',fontWeight:'bold'}}>@{opponent||'...'} ({mySymbol==='X'?'O':'X'})</span>
      </div>
      <div style={{fontWeight:'bold',fontSize:'15px',color:turn===mySymbol?'#10b981':'#0d9488'}}>
        {turn===mySymbol ? '✅ Your Turn' : `⏳ @${opponent||'opponent'}'s Turn`}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
        {board.map((cell,i) => (
          <Cell key={i} value={cell} isWin={result?.line?.includes(i)}
            onClick={() => makeMove(i)} size={cs} />
        ))}
      </div>
      <div style={{fontSize:'11px',color:'#999'}}>Room: {roomCode}</div>
      <button style={{...G.btn,backgroundColor:'#ff4444',fontSize:'12px',padding:'6px 14px'}} onClick={exit}>Exit</button>
    </div>
  );
}
